/**
 * Blackwaxx&Co collection filtering. Chips, "In stock only", sort and pagination update the toolbar and the grid in place:
 * the section is re-rendered through the Section Rendering API and only [data-bw-toolbar] and [data-bw-results] are
 * swapped, with history.pushState so the URL, back and forward keep working. Every control is a plain link or form
 * control first, so it still works as a normal page load without JS.
 */
class BwCollection extends HTMLElement {
  #controller = null;
  #cache = new Map();

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
    this.addEventListener('pointerover', this.#onPrefetch);
    window.addEventListener('popstate', this.#onPopState);
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.#onPopState);
  }

  get sectionId() {
    return this.dataset.sectionId;
  }

  #onClick = (event) => {
    const link = event.target.closest?.('a[data-bw-link]');
    if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    if (link.getAttribute('aria-disabled') === 'true') return event.preventDefault();
    event.preventDefault();
    this.navigate(link.href, { focusKey: link.textContent.trim() });
  };

  #onChange = (event) => {
    const target = event.target;
    if (target.matches?.('[data-bw-stock]')) {
      this.navigate(new URL(target.checked ? target.dataset.urlOn : target.dataset.urlOff, location.origin).href, {
        focusSelector: '[data-bw-stock]',
      });
    } else if (target.matches?.('[data-bw-sort]')) {
      const url = new URL(location.href);
      url.searchParams.set('sort_by', target.value);
      url.searchParams.delete('page');
      this.navigate(url.href, { focusSelector: '[data-bw-sort]' });
    }
  };

  #onPrefetch = (event) => {
    const link = event.target.closest?.('a[data-bw-link]');
    if (link && !this.#cache.has(link.href)) this.#cache.set(link.href, this.#fetch(link.href).catch(() => null));
  };

  #onPopState = () => {
    this.#swap(location.href, false);
  };

  navigate(href, focus = {}) {
    return this.#swap(href, true, focus);
  }

  #sectionUrl(href) {
    const url = new URL(href, location.origin);
    url.searchParams.set('section_id', this.sectionId);
    return url.href;
  }

  async #fetch(href) {
    const html = await fetch(this.#sectionUrl(href), { credentials: 'same-origin' }).then((r) => r.text());
    return new DOMParser().parseFromString(html, 'text/html').querySelector('bw-collection');
  }

  async #swap(href, push, focus = {}) {
    const results = this.querySelector('[data-bw-results]');
    this.#controller?.abort();
    this.#controller = new AbortController();
    results?.setAttribute('aria-busy', 'true');

    try {
      let next = await (this.#cache.get(href) ?? this.#fetch(href));
      this.#cache.delete(href);
      if (!next) throw new Error('No section returned');

      for (const selector of ['[data-bw-toolbar]', '[data-bw-results]']) {
        const from = next.querySelector(selector);
        const to = this.querySelector(selector);
        if (from && to) to.replaceWith(from);
      }
      if (push) history.pushState({}, '', href);

      const focusTarget =
        (focus.focusSelector && this.querySelector(focus.focusSelector)) ||
        (focus.focusKey && [...this.querySelectorAll('.bw-chip, .bw-btn')].find((el) => el.textContent.trim() === focus.focusKey));
      focusTarget?.focus({ preventScroll: true });
    } catch (error) {
      if (error?.name !== 'AbortError') location.href = href;
    } finally {
      this.querySelector('[data-bw-results]')?.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('bw-collection')) customElements.define('bw-collection', BwCollection);
